/**
 * Rent detail drawer: uses shared DetailDrawer + DetailDrawerContent, compact rows, i18n.
 * Quick links: Powerbanks, Users, Stations (start/return station).
 */
import React, { useCallback, useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Tooltip from '@mui/material/Tooltip';
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import dayjs from 'dayjs';
import useNotifications from '../mui-crud/hooks/useNotifications/useNotifications';
import * as api from '../../modules/api';
import {
  getRentCompletionPlace,
  getRentCurrencySymbol,
  getRentPlaceAddress,
  getRentPlacePointName,
  getRentPlaceStationNumber,
  getRentStartPlace,
  getRentTariffCrossLinkQuery,
  getRentTariffLabel,
  resolveCompletionStationNumber,
} from '../utils/rentDisplay';
import { setCrossLinkPowerbankId } from '../../modules/powerbanks/powerbanksAction';
import DetailDrawer from '../components/DetailDrawer';
import { Section } from '../components/DetailDrawerContent';
import EntityCommentsSection, { normalizeEntityComments } from '../components/EntityCommentsSection';
import {
  canAccessTariffsSection,
  canAccessUsersSection,
  getCurrentRole,
  isSuperAdmin,
} from '../utils/accessControl';

function formatDate(value) {
  if (value == null || value === '') return '—';
  if (Array.isArray(value)) {
    const [y, m, d, h = 0, min = 0, s = 0] = value;
    const date = new Date(y, (m || 1) - 1, d || 1, h, min, s);
    return dayjs(date).format('DD.MM.YYYY HH:mm');
  }
  const d = dayjs(value);
  return d.isValid() ? d.format('DD.MM.YYYY HH:mm') : '—';
}

function parseRentDate(value) {
  if (value == null || value === '') return null;
  if (Array.isArray(value)) {
    const [y, m, d, h = 0, min = 0, s = 0] = value;
    return new Date(y, (m || 1) - 1, d || 1, h, min, s);
  }
  const d = dayjs(value);
  return d.isValid() ? d.toDate() : null;
}

function getTotalRentTime(startDate, finishedDate) {
  const start = parseRentDate(startDate);
  const end = parseRentDate(finishedDate);
  if (!start || !end) return null;
  const ms = end.getTime() - start.getTime();
  if (ms < 0) return '—';
  const sec = Math.floor(ms / 1000) % 60;
  const min = Math.floor(ms / 60000) % 60;
  const hours = Math.floor(ms / 3600000) % 24;
  const days = Math.floor(ms / 86400000);
  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (min > 0) parts.push(`${min}m`);
  if (sec > 0 || parts.length === 0) parts.push(`${sec}s`);
  return parts.join(' ');
}

/** Compact row: label + value (clickable to copy) + optional open-in buttons. No copy icon. */
function CompactRow({
  label,
  value,
  onCopy,
  onOpenInPowerbanks,
  onOpenInUsers,
  onOpenInStations,
  onOpenInTariffs,
  emptyPlaceholder,
}) {
  const { t } = useTranslation();
  const raw = value != null && value !== '' ? String(value) : (emptyPlaceholder ?? '—');
  const copyable = value != null && value !== '' && onCopy;
  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: 0.5,
        py: 0.75,
        width: '100%',
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ flex: '0 0 auto', minWidth: 0 }}>
        {label}:
      </Typography>
      {copyable ? (
        <Tooltip title={t('drawer.copy')}>
          <Button
            variant="text"
            color="primary"
            size="small"
            onClick={() => onCopy(String(value))}
            aria-label={t('drawer.copy')}
            sx={{
              textTransform: 'none',
              wordBreak: 'break-word',
              minWidth: 0,
              flex: '0 1 auto',
              justifyContent: 'flex-start',
              textAlign: 'left',
              py: 0,
              px: 0,
              minHeight: 'auto',
              lineHeight: 1.43,
            }}
          >
            {raw}
          </Button>
        </Tooltip>
      ) : (
        <Typography
          variant="body2"
          sx={{ flex: '0 1 auto', wordBreak: 'break-word', minWidth: 0, textAlign: 'left' }}
        >
          {raw}
        </Typography>
      )}
      <Stack direction="row" alignItems="center" spacing={0} sx={{ flexShrink: 0 }}>
        {typeof onOpenInStations === 'function' && (
          <Tooltip title={t('drawer.rent.openInStations')}>
            <IconButton size="small" aria-label={t('drawer.rent.openInStations')} onClick={onOpenInStations}>
              <OpenInNewOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {onOpenInPowerbanks && value != null && value !== '' && (
          <Tooltip title={t('drawer.rent.openInPowerbanks')}>
            <IconButton size="small" aria-label={t('drawer.rent.openInPowerbanks')} onClick={onOpenInPowerbanks}>
              <OpenInNewOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {onOpenInUsers && (value != null && value !== '' || onOpenInUsers) && (
          <Tooltip title={t('drawer.rent.openInUsers')}>
            <IconButton size="small" aria-label={t('drawer.rent.openInUsers')} onClick={onOpenInUsers}>
              <OpenInNewOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {onOpenInTariffs && value != null && value !== '' && (
          <Tooltip title={t('drawer.rent.openInTariffs')}>
            <IconButton size="small" aria-label={t('drawer.rent.openInTariffs')} onClick={onOpenInTariffs}>
              <OpenInNewOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </Box>
  );
}

export default function RentDetailDrawer({ rent, open, onClose, onRefresh }) {
  const { t } = useTranslation();
  const notifications = useNotifications();
  const dispatch = useDispatch();
  const role = getCurrentRole();
  const canOpenUsers = canAccessUsersSection(role);
  const canOpenTariffs = canAccessTariffsSection(role);
  const canStopRent = isSuperAdmin(role);
  const canCrossLinkNetwork = isSuperAdmin(role) || role === 'ADMIN' || role === 'MERCHANT';
  // Station/powerbank deep-links: only when list pages will enforce ownership (ADMIN/MERCHANT/SA).
  // Users / stop rent: SUPER_ADMIN only.
  const [stopRentDialogOpen, setStopRentDialogOpen] = useState(false);
  const [stopRentLoading, setStopRentLoading] = useState(false);
  const [comments, setComments] = useState([]);

  useEffect(() => {
    if (!open || !rent) return;
    setComments(normalizeEntityComments(rent));
  }, [open, rent?.id, rent?.comment, rent?.comments]);

  const handleCopy = useCallback(
    (text) => {
      if (!text || text === '—') return;
      navigator.clipboard.writeText(String(text)).then(
        () => notifications.show(t('drawer.copied'), { severity: 'success', autoHideDuration: 2000 }),
        () => notifications.show(t('drawer.copyFailed'), { severity: 'error' }),
      );
    },
    [notifications, t],
  );

  const handleStopRentClick = useCallback(() => {
    if (!canStopRent) return;
    setStopRentDialogOpen(true);
  }, [canStopRent]);
  const handleStopRentConfirm = useCallback(async () => {
    if (!canStopRent || !rent?.id) return;
    setStopRentLoading(true);
    try {
      await api.BlockRent(rent.id);
      setStopRentDialogOpen(false);
      onRefresh?.();
      onClose();
      notifications.show(t('drawer.rent.rentStopped'), { severity: 'success', autoHideDuration: 3000 });
    } catch (e) {
      notifications.show(e?.message || t('drawer.rent.failedToStopRent'), { severity: 'error' });
    } finally {
      setStopRentLoading(false);
    }
  }, [canStopRent, rent?.id, onRefresh, onClose, notifications, t]);

  const handleAddComment = useCallback(async (text) => {
    if (!rent?.id) return;
    const res = await api.commentRent(rent.id, text);
    const list = res?.data?.comments;
    if (Array.isArray(list)) setComments(list);
    else setComments((prev) => [...prev, { at: new Date().toISOString(), by: null, text }]);
    notifications.show(t('drawer.comments.added'), { severity: 'success', autoHideDuration: 2000 });
  }, [rent?.id, notifications, t]);

  const handleOpenInPowerbanks = useCallback(() => {
    if (!canCrossLinkNetwork || !rent?.powerBankID) return;
    dispatch(setCrossLinkPowerbankId(rent.powerBankID));
    window.location.hash = '#/powerBank';
  }, [rent?.powerBankID, dispatch, canCrossLinkNetwork]);

  const handleOpenInUsers = useCallback(() => {
    if (!canOpenUsers) {
      notifications.show(t('access.sectionForbidden'), { severity: 'warning', autoHideDuration: 3000 });
      return;
    }
    const id = rent?.user?.id;
    const phone = rent?.user?.phoneNumber;
    const identifier = id != null && id !== '' ? String(id) : phone;
    if (identifier) {
      try {
        sessionStorage.setItem('crossLinkUser', identifier);
      } catch (_) {}
      window.location.hash = '#/users';
    }
  }, [rent?.user?.id, rent?.user?.phoneNumber, canOpenUsers, notifications, t]);

  const handleOpenInStations = useCallback((stationNumberOrId) => {
    if (!canCrossLinkNetwork || stationNumberOrId == null || stationNumberOrId === '') return;
    try {
      sessionStorage.setItem('crossLinkStation', String(stationNumberOrId));
    } catch (_) {}
    window.location.hash = '#/station';
  }, [canCrossLinkNetwork]);

  const handleOpenInTariffs = useCallback(() => {
    if (!canOpenTariffs) {
      notifications.show(t('access.sectionForbidden'), { severity: 'warning', autoHideDuration: 3000 });
      return;
    }
    const query = getRentTariffCrossLinkQuery(rent);
    if (!query) return;
    try {
      sessionStorage.setItem('crossLinkTariffName', String(query));
    } catch (_) {}
    window.location.hash = '#/tariff';
  }, [canOpenTariffs, rent, notifications, t]);

  const finishDate = rent?.finishedDate ?? '';
  const statusLabel =
    rent?.rentStatus === 'FINISHED' ? t('drawer.rent.finished') :
    rent?.rentStatus === 'FAILED' ? t('drawer.rent.failed') :
    rent?.rentStatus === 'PROCESSING' ? t('drawer.rent.inProgress') : rent?.rentStatus || '—';
  const statusColor = rent?.rentStatus === 'FINISHED' ? 'default' : rent?.rentStatus === 'FAILED' ? 'error' : 'success';
  const currency = getRentCurrencySymbol(rent);
  const totalTime = getTotalRentTime(rent?.startDate, rent?.finishedDate);
  const tariffLabel = getRentTariffLabel(rent);
  const startPlace = getRentStartPlace(rent);
  const completionPlace = getRentCompletionPlace(rent);
  const startStationNumber = getRentPlaceStationNumber(startPlace, rent?.stationNumber);
  const startPointName = getRentPlacePointName(startPlace, rent?.startStation);
  const startAddress = getRentPlaceAddress(startPlace, rent?.startStation);
  const completionStationNumber = resolveCompletionStationNumber(rent);
  const completionPointName = getRentPlacePointName(completionPlace, rent?.completionStation, { legacyJoined: true });
  const completionAddress = getRentPlaceAddress(completionPlace, rent?.completionStation, { legacyJoined: true });
  const openReturnStation =
    canCrossLinkNetwork && completionStationNumber
      ? () => handleOpenInStations(completionStationNumber)
      : undefined;
  const returnEmptyPlaceholder =
    rent?.finishedDate || rent?.rentStatus === 'FINISHED' || rent?.rentStatus === 'FAILED'
      ? t('drawer.rent.returnUnknown')
      : t('drawer.rent.inProgress');

  return (
    <>
      <DetailDrawer title={t('drawer.rent.title')} open={open} onClose={onClose}>
        {!rent ? (
          <Typography color="text.secondary">{t('drawer.rent.noSelection')}</Typography>
        ) : (
        <Stack spacing={2}>
          {/* Блок 1: Детали аренды — ID, повербанк, время, сумма, тариф, статус */}
          <Section title={t('drawer.rent.details')}>
            <CompactRow label={t('drawer.rent.id')} value={rent.id} onCopy={handleCopy} />
            <CompactRow
              label={t('drawer.rent.powerbankId')}
              value={rent.powerBankID}
              onCopy={handleCopy}
              onOpenInPowerbanks={canCrossLinkNetwork && rent.powerBankID ? handleOpenInPowerbanks : undefined}
            />
            <CompactRow
              label={t('drawer.rent.startOfRent')}
              value={formatDate(rent.startDate)}
              onCopy={handleCopy}
            />
            <CompactRow
              label={t('drawer.rent.endOfRent')}
              value={finishDate ? formatDate(finishDate) : t('drawer.rent.inProgress')}
              onCopy={finishDate ? handleCopy : undefined}
            />
            {totalTime != null && (
              <CompactRow
                label={t('drawer.rent.totalRentTime')}
                value={totalTime === 'In progress' ? t('drawer.rent.inProgress') : totalTime}
                onCopy={handleCopy}
              />
            )}
            <CompactRow
              label={t('drawer.rent.totalCost')}
              value={rent.totalAmount != null ? `${rent.totalAmount} ${currency}` : '—'}
              onCopy={rent.totalAmount != null ? handleCopy : undefined}
            />
            <CompactRow
              label={t('drawer.rent.debt')}
              value={rent.debt != null ? `${rent.debt} ${currency}` : `0 ${currency}`}
              onCopy={handleCopy}
            />
            <CompactRow
              label={t('drawer.rent.currentTariff')}
              value={tariffLabel}
              onCopy={handleCopy}
              onOpenInTariffs={canOpenTariffs && tariffLabel ? handleOpenInTariffs : undefined}
            />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, pt: 0.5, mt: 0.5 }}>
              <Typography variant="caption" color="text.secondary">{t('drawer.rent.status')}:</Typography>
              <Chip label={statusLabel} color={statusColor} size="small" />
              {rent.rentStatus === 'FAILED' && rent.failReason && (
                <Typography variant="body2" color="error" sx={{ width: '100%', mt: 0.5 }}>{rent.failReason}</Typography>
              )}
            </Box>
          </Section>

          {canStopRent && rent.rentStatus !== 'FINISHED' && rent.rentStatus !== 'FAILED' && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<StopCircleOutlinedIcon />}
              onClick={handleStopRentClick}
              fullWidth
            >
              {t('drawer.rent.stopRent')}
            </Button>
          )}

          {/* Блок 2: Станции — начало и возврат симметрично (place + legacy fallback) */}
          <Section title={t('drawer.rent.stations')}>
            <CompactRow
              label={t('drawer.rent.stationNumber')}
              value={startStationNumber}
              onCopy={handleCopy}
              onOpenInStations={canCrossLinkNetwork && startStationNumber ? () => handleOpenInStations(startStationNumber) : undefined}
            />
            <CompactRow
              label={t('drawer.rent.placementPoint')}
              value={startPointName}
              onCopy={handleCopy}
            />
            <CompactRow
              label={t('drawer.rent.startStation')}
              value={startAddress}
              onCopy={handleCopy}
              onOpenInStations={canCrossLinkNetwork && startStationNumber ? () => handleOpenInStations(startStationNumber) : undefined}
            />
            <CompactRow
              label={t('drawer.rent.startSlot')}
              value={rent.stationSlot}
            />
            <CompactRow
              label={t('drawer.rent.returnStationNumber')}
              value={completionStationNumber}
              emptyPlaceholder={returnEmptyPlaceholder}
              onCopy={handleCopy}
              onOpenInStations={openReturnStation}
            />
            <CompactRow
              label={t('drawer.rent.returnPlacementPoint')}
              value={completionPointName}
              emptyPlaceholder={returnEmptyPlaceholder}
              onCopy={handleCopy}
            />
            <CompactRow
              label={t('drawer.rent.returnStation')}
              value={completionAddress}
              emptyPlaceholder={returnEmptyPlaceholder}
              onCopy={handleCopy}
              onOpenInStations={openReturnStation}
            />
            <CompactRow
              label={t('drawer.rent.returnSlot')}
              value={rent.completionStationSlot}
              emptyPlaceholder={returnEmptyPlaceholder}
            />
          </Section>

          {/* Блок 3: Пользователь */}
          <Section title={t('drawer.rent.user')}>
            <CompactRow
              label={t('drawer.rent.userId')}
              value={rent.user?.id}
              onCopy={handleCopy}
              onOpenInUsers={canOpenUsers && (rent.user?.id || rent.user?.phoneNumber) ? handleOpenInUsers : undefined}
            />
            <CompactRow
              label={t('drawer.rent.phone')}
              value={rent.user?.phoneNumber}
              onCopy={handleCopy}
              onOpenInUsers={canOpenUsers && (rent.user?.id || rent.user?.phoneNumber) ? handleOpenInUsers : undefined}
            />
          </Section>

          {/* Блок 4: Комментарии */}
          <EntityCommentsSection
            comments={comments}
            onAdd={handleAddComment}
          />
        </Stack>
        )}
      </DetailDrawer>

      <Dialog open={stopRentDialogOpen} onClose={() => !stopRentLoading && setStopRentDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('drawer.rent.stopRent')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('drawer.rent.stopRentConfirm', { id: rent?.id })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStopRentDialogOpen(false)} disabled={stopRentLoading}>{t('drawer.cancel')}</Button>
          <Button variant="contained" color="error" onClick={handleStopRentConfirm} disabled={stopRentLoading}>
            {stopRentLoading ? '…' : t('drawer.rent.stopRent')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
